import { Checkbox, Tooltip, Popover } from 'antd';
import { CloseFilled, Layers } from '@carbon/icons-react';
import { useEffect, useMemo, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BlockDiagramCard from '@/components/Layouts/DashboardLayout/BlockDiagramCard';
import Icon from '@ant-design/icons';
import { BlockDiagramIconSvg } from '@/assets/svg';
import { showNavItem } from '@/layout/DashboardLayout/constants';
import HydraulicProfilerCard from '@/components/Layouts/DashboardLayout/HydraulicProfilerCard';
import DashboardContext from '@/contexts/DashboardContext';
import { useDispatch } from 'react-redux';
import { restartResumeAndReadingList } from '@/redux/slices/readingsResumesSlice';
import { ChartWaterfall } from '@carbon/icons-react';
import MapIcon from '@/layout/DashboardLayout/Icons';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';

export default function LayerList({
	infraTypes,
	handleSelectedInfraTypes,
}: {
	infraTypes: string[];
	handleSelectedInfraTypes: (infraTypes: string[]) => void;
}) {
	const [expanded, setExpanded] = useState<boolean>(false);
	const [selectedInfraTypes, setSelectedInfraTypes] = useState<string[]>([]);
	const [openBlockDialog, setOpenBlockDialog] = useState(false);
	const location = useLocation();
	const [openHydraulicProfilerDialog, setOpenHydraulicProfilerDialog] = useState(false);
	const { value: contextValue, setValue: setContextValue } = useContext(DashboardContext);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const handleOpenBlockDialogChange = (val: boolean) => {
		setOpenBlockDialog(val);
	};
	const optionsLayers = useMemo(() => {
		return infraTypes;
	}, [infraTypes]);

	useEffect(() => {
		setSelectedInfraTypes(infraTypes);
		handleSelectedInfraTypes(infraTypes);
	}, [infraTypes]);

	return expanded ? (
		<div className="flex flex-col min-w-[130px] min-h-[150px] max-h-[200px]">
			<div className="mb-2 flex justify-between items-center">
				<span className="text-black font-bold text-xl">Layers</span>
				<Tooltip title="Close">
					<CloseFilled
						className="fill-blue-950 cursor-pointer"
						size="24px"
						onClick={() => {
							setExpanded(false);
						}}
					/>
				</Tooltip>
			</div>
			<div className="layer-list flex flex-col overflow-y-auto">
				{optionsLayers.length > 0 ? (
					<Checkbox.Group
						className="flex flex-col"
						options={optionsLayers.map((option) => {
							return {
								label: option
									.toLowerCase()
									.split(' ')
									.map((word) => {
										return word.charAt(0).toUpperCase() + word.slice(1);
									})
									.join(' '),
								value: option,
							};
						})}
						value={selectedInfraTypes}
						onChange={(list) => {
							setSelectedInfraTypes(list as string[]);
							handleSelectedInfraTypes(list as string[]);
						}}
					/>
				) : (
					<span>No assets found</span>
				)}
			</div>
		</div>
	) : (
		<>
			<div className="flex justify-center w-full" style={{ marginBottom: '16px' }}>
				<Tooltip placement="left" title="Default Map">
					<MapIcon
						size={28}
						className="cursor-pointer"
						style={{ color: '#74788d' }}
						onClick={() => {
							navigate('/');
							dispatch(restartResumeAndReadingList() as any);
							setContextValue((prev) => ({ ...prev, selectedSensors: [] }));
							dashboardMapEventsService.setSubject({
								type: 'setMode',
								data: 'default',
							});
						}}
					/>
				</Tooltip>
			</div>

			{showNavItem(process.env.VITE_HEADER_SHOW_BLOCK_DIAGRAM, location.pathname) && (
				<div className="flex justify-center w-full" style={{ marginBottom: '16px' }}>
					<Popover
						placement="left"
						content={
							<div
								style={{
									width: '300px',
									maxHeight: '300px',
									overflowX: 'auto',
								}}
							>
								<BlockDiagramCard
									onClickRow={() => {
										setOpenBlockDialog(false);
									}}
								/>
							</div>
						}
						title="Select a Block Diagram"
						open={openBlockDialog}
						onOpenChange={handleOpenBlockDialogChange}
						trigger="click"
					>
						<Tooltip placement="left" title="Block Diagrams">
							<Icon
								component={BlockDiagramIconSvg}
								style={{
									fontSize: '32px',
									cursor: 'pointer',
									display: 'block',
								}}
								rev={undefined}
							/>
						</Tooltip>
					</Popover>
				</div>
			)}

			<div className="flex justify-center w-full" style={{ marginBottom: '16px' }}>
				<Popover
					placement="left"
					content={
						<div
							style={{
								width: '300px',
								maxHeight: '300px',
								overflowX: 'auto',
							}}
						>
							<HydraulicProfilerCard
								locationId={contextValue.selectedLocation}
								onClickRow={() => {
									setOpenHydraulicProfilerDialog(false);
								}}
							/>
						</div>
					}
					title="Select a Hydraulic Profiler"
					open={openHydraulicProfilerDialog}
					onOpenChange={(val) => {
						setOpenHydraulicProfilerDialog(val);
					}}
					trigger="click"
				>
					<Tooltip placement="left" title="Hydraulic Profiles">
						<ChartWaterfall size={28} className="fill-[#74788d] cursor-pointer" />
					</Tooltip>
				</Popover>
			</div>

			<Tooltip placement="left" title="Layers">
				<Layers
					size={32}
					className="cursor-pointer"
					onClick={() => {
						setExpanded(true);
					}}
				/>
			</Tooltip>
		</>
	);
}
